import React from "react"
import { Field as FieldPrimitive } from "@base-ui/react/field"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../utils"
import { FieldContext } from "./field-context"

// =============================================================================
// Styles
// =============================================================================
const DEFAULT_ORIENTATION = "vertical" as const

// The label greys off the field rather than off a sibling, because the two
// orientations put the control on either side of it. A field holding items
// greys nothing itself: each `Field.Item` owns its own label, so that one
// disabled option does not grey the whole group.
const fieldStyles = cva(
  [
    "flex gap-2",
    "not-has-[[data-slot=field-item]]:has-data-disabled:[&_[data-slot=field-label]]:text-gray-600",
    "not-has-[[data-slot=field-item]]:has-data-disabled:[&_[data-slot=field-description]]:text-gray-600"
  ],
  {
    variants: {
      orientation: {
        vertical: "flex-col",
        horizontal: "items-start"
      }
    },
    defaultVariants: {
      orientation: DEFAULT_ORIENTATION
    }
  }
)

export type FieldOrientation = NonNullable<
  VariantProps<typeof fieldStyles>["orientation"]
>

// =============================================================================
// Props
// =============================================================================
export type FieldRootProps = {
  /** A control and its `Field.Label`, in the order they should appear */
  children?: React.ReactNode
  /** Identifies the field when its form is submitted */
  name?: string
  /**
   * `vertical` stacks the label above the control (text inputs, selects).
   * `horizontal` sits them side by side (checkboxes, radios, toggles).
   */
  orientation?: FieldOrientation
  /**
   * Disables the control and greys its label.
   *
   * Setting it here or on the control does the same thing: the two are OR'd,
   * so neither overrides the other, and the label greys either way.
   */
  disabled?: boolean
  /**
   * Marks the value as rejected: the control gets `aria-invalid` and
   * `Field.Error` shows. Set it from wherever the error lives, for example the
   * surrounding form's error for this field.
   */
  invalid?: boolean
  /** Additional CSS classes for layout */
  className?: string
}

// =============================================================================
// Component
// =============================================================================
/**
 * Groups a form control with its label and owns the state they share.
 *
 * The control registers its id with the field, so `Field.Label` resolves its
 * own `htmlFor` and the control gets an accessible name without either side
 * being wired up by hand.
 */
function Field({
  children,
  name,
  orientation = DEFAULT_ORIENTATION,
  disabled,
  invalid = false,
  className
}: FieldRootProps) {
  const labelId = React.useId()

  return (
    <FieldContext.Provider value={{ invalid, labelId }}>
      <FieldPrimitive.Root
        data-slot="field"
        name={name}
        // Published so a part can style off the orientation without knowing
        // which classes produced it, the way Base UI projects its own state.
        data-orientation={orientation}
        disabled={disabled}
        invalid={invalid}
        className={cn(fieldStyles({ orientation }), className)}
      >
        {children}
      </FieldPrimitive.Root>
    </FieldContext.Provider>
  )
}

Field.displayName = "Field"

export { Field as FieldRoot }
